/**
 * Phase 7 Slice A.2 — Ad-Spend page.
 *
 * Operators land here to record ad-spend events (manually or via CSV upload)
 * so the cockpit can produce real ROAS / CPL. See ADR-0033.
 *
 * - KPI strip: total spend, event count, avg per event, ROAS/CPL (when the
 *   cockpit summary can be reached).
 * - Filter bar: period preset + platform filter.
 * - Actions: Upload CSV / Manual entry / Download template.
 * - Events table: paginated, sortable by date desc.
 *
 * All data-testid attributes follow the kebab-case convention used elsewhere.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Euro, Upload, Plus, Download, ExternalLink } from "lucide-react";
import { api, API_BASE, auth } from "../../lib/api";
import { useT } from "../../lib/i18n";
import {
  Card, CardContent, CardHeader, CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";

const PLATFORMS = [
  "meta", "google", "linkedin", "tiktok", "youtube", "organic", "other",
];
const PRESETS = [
  { key: "7d", days: 7 },
  { key: "30d", days: 30 },
  { key: "90d", days: 90 },
  { key: "365d", days: 365 },
];

function isoDate(d) { return d.toISOString().slice(0, 10); }
function todayIso() { return isoDate(new Date()); }
function daysAgoIso(days) {
  const d = new Date(); d.setUTCDate(d.getUTCDate() - days); return isoDate(d);
}
function fmtEur(n) {
  if (n === null || n === undefined) return "—";
  return new Intl.NumberFormat("de-DE", {
    style: "currency", currency: "EUR", maximumFractionDigits: 2,
  }).format(n);
}
function fmtInt(n) {
  if (n === null || n === undefined || n === "") return "—";
  return new Intl.NumberFormat("de-DE").format(Number(n));
}

// ---------- Manual-entry modal ----------

function ManualEntryDialog({ open, onClose, onSaved }) {
  const t = useT();
  const [form, setForm] = useState({
    occurred_on: todayIso(), campaign_id: "", platform: "meta",
    ad_id: "", spend_eur: "", impressions: "", clicks: "", conversions: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        occurred_on: form.occurred_on, campaign_id: form.campaign_id.trim(),
        platform: form.platform, spend_eur: Number(form.spend_eur),
      };
      if (form.ad_id.trim()) payload.ad_id = form.ad_id.trim();
      if (form.impressions) payload.impressions = Number(form.impressions);
      if (form.clicks) payload.clicks = Number(form.clicks);
      if (form.conversions) payload.conversions = Number(form.conversions);
      if (form.notes.trim()) payload.notes = form.notes.trim();
      await api.post("/ad-spend/events", payload);
      toast.success(t("ad_spend.saved", "Ad-spend entry saved."));
      onSaved();
      onClose();
    } catch (e) {
      toast.error(t("ad_spend.save_error", "Save failed."));
    } finally {
      setSubmitting(false);
    }
  };

  const valid = form.campaign_id.trim() && Number(form.spend_eur) >= 0
      && form.occurred_on;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="ad-spend-manual-dialog">
        <DialogHeader>
          <DialogTitle>{t("ad_spend.modal_manual_title", "Record ad spend")}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>{t("ad_spend.field_date", "Date")}</Label>
            <Input type="date" value={form.occurred_on}
                   onChange={(e) => setF("occurred_on", e.target.value)}
                   data-testid="manual-occurred-on" />
          </div>
          <div>
            <Label>{t("ad_spend.field_platform", "Platform")}</Label>
            <Select value={form.platform}
                    onValueChange={(v) => setF("platform", v)}>
              <SelectTrigger data-testid="manual-platform"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PLATFORMS.map((p) => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>{t("ad_spend.field_campaign_id", "Campaign")}</Label>
            <Input placeholder="campaign-id" value={form.campaign_id}
                   onChange={(e) => setF("campaign_id", e.target.value)}
                   data-testid="manual-campaign-id" />
          </div>
          <div>
            <Label>{t("ad_spend.field_ad_id", "Ad ID (optional)")}</Label>
            <Input value={form.ad_id}
                   onChange={(e) => setF("ad_id", e.target.value)}
                   data-testid="manual-ad-id" />
          </div>
          <div>
            <Label>{t("ad_spend.field_spend_eur", "Spend (€)")}</Label>
            <Input type="number" step="0.01" value={form.spend_eur}
                   onChange={(e) => setF("spend_eur", e.target.value)}
                   data-testid="manual-spend-eur" />
          </div>
          <div>
            <Label>{t("ad_spend.field_impressions", "Impressions")}</Label>
            <Input type="number" value={form.impressions}
                   onChange={(e) => setF("impressions", e.target.value)}
                   data-testid="manual-impressions" />
          </div>
          <div>
            <Label>{t("ad_spend.field_clicks", "Clicks")}</Label>
            <Input type="number" value={form.clicks}
                   onChange={(e) => setF("clicks", e.target.value)}
                   data-testid="manual-clicks" />
          </div>
          <div>
            <Label>{t("ad_spend.field_conversions", "Conversions")}</Label>
            <Input type="number" value={form.conversions}
                   onChange={(e) => setF("conversions", e.target.value)}
                   data-testid="manual-conversions" />
          </div>
          <div className="col-span-2">
            <Label>{t("ad_spend.field_notes", "Notes")}</Label>
            <Input value={form.notes}
                   onChange={(e) => setF("notes", e.target.value)}
                   data-testid="manual-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}
                  data-testid="manual-cancel">
            {t("common.cancel", "Cancel")}
          </Button>
          <Button disabled={!valid || submitting} onClick={submit}
                  data-testid="manual-submit">
            {submitting ? t("common.saving", "Saving…")
                        : t("ad_spend.submit", "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- CSV upload modal ----------

function CsvUploadDialog({ open, onClose, onUploaded }) {
  const t = useT();
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!file) return;
    setSubmitting(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/ad-spend/events/csv-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      toast.success(t("ad_spend.upload_success",
            "{{inserted}} rows imported, {{skipped}} duplicates skipped, {{rejected}} rejected.")
          .replace("{{inserted}}", r.data.inserted)
          .replace("{{skipped}}", r.data.skipped_duplicate)
          .replace("{{rejected}}", r.data.rejected));
      onUploaded();
    } catch (e) {
      toast.error(t("ad_spend.upload_error", "Upload failed."));
      setResult({ error: e?.response?.data || true });
    } finally {
      setSubmitting(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const r = await fetch(`${API_BASE}/ad-spend/template.csv`, {
        headers: {
          Authorization: `Bearer ${auth.getAccess()}`,
          "X-Tenant-Id": auth.getTenant(),
        },
      });
      if (!r.ok) throw new Error("template_failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "ad-spend-template.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(t("ad_spend.template_downloaded", "Template downloaded."));
    } catch (e) {
      toast.error(t("ad_spend.upload_error", "Upload failed."));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="ad-spend-csv-dialog">
        <DialogHeader>
          <DialogTitle>{t("ad_spend.modal_csv_title", "Upload CSV")}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {t("ad_spend.modal_csv_hint",
                "Required columns: date, campaign_id, platform, spend_eur. Optional: ad_id, currency, impressions, clicks, conversions.")}
        </p>
        <div className="flex items-center gap-2">
          <Input type="file" accept=".csv,text/csv"
                 onChange={(e) => setFile(e.target.files?.[0] || null)}
                 data-testid="csv-file-input" />
          <Button variant="outline" size="sm" onClick={downloadTemplate}
                  data-testid="csv-download-template">
            <Download className="h-4 w-4 mr-1" />
            {t("ad_spend.action_download_template", "Download template")}
          </Button>
        </div>
        {result && !result.error && (
          <div className="text-sm rounded border bg-muted/30 p-3 space-y-1"
               data-testid="csv-result-panel">
            <div>{t("ad_spend.upload_success",
                "{{inserted}} rows imported, {{skipped}} duplicates skipped, {{rejected}} rejected.")
              .replace("{{inserted}}", result.inserted)
              .replace("{{skipped}}", result.skipped_duplicate)
              .replace("{{rejected}}", result.rejected)}</div>
            {(result.errors || []).length > 0 && (
              <div className="space-y-1 mt-2">
                {result.errors.slice(0, 10).map((e, i) => (
                  <div key={i} className="text-xs text-rose-600"
                       data-testid={`csv-error-row-${e.row}`}>
                    {`Row ${e.row}: ${e.error}`}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}
                  data-testid="csv-cancel">
            {t("common.cancel", "Cancel")}
          </Button>
          <Button disabled={!file || submitting} onClick={submit}
                  data-testid="csv-submit">
            {submitting ? t("common.saving", "Saving…")
                        : t("ad_spend.action_upload_csv", "Upload CSV")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Main page ----------

export default function AdSpend() {
  const t = useT();
  const [preset, setPreset] = useState("365d");  // see the seed by default
  const [platformFilter, setPlatformFilter] = useState("all");
  const [data, setData] = useState({ items: [], total: 0 });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [manualOpen, setManualOpen] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  const windowQ = useMemo(() => {
    const p = PRESETS.find((x) => x.key === preset) || PRESETS[3];
    return { since: daysAgoIso(p.days), until: todayIso() };
  }, [preset]);

  useEffect(() => {
    setLoading(true);
    const params = {
      since: windowQ.since, until: windowQ.until, limit: 50,
    };
    if (platformFilter !== "all") params.platform = platformFilter;
    Promise.all([
      api.get("/ad-spend/events", { params }),
      api.get("/ad-spend/summary", { params: {
        since: windowQ.since, until: windowQ.until,
        ...(platformFilter !== "all" ? { platform: platformFilter } : {}),
      }}),
    ]).then(([events, sum]) => {
      setData(events.data); setSummary(sum.data);
    }).catch(() => {
      setData({ items: [], total: 0 }); setSummary(null);
    }).finally(() => setLoading(false));
  }, [windowQ.since, windowQ.until, platformFilter, reloadTick]);

  const refresh = () => setReloadTick((x) => x + 1);
  const totalSpend = summary?.total_spend_eur ?? 0;
  const eventCount = summary?.event_count ?? 0;
  const avgPerEvent = eventCount > 0 ? totalSpend / eventCount : null;
  const topPlatforms = (summary?.by_platform || []).slice(0, 4);

  return (
    <div className="p-6 space-y-6" data-testid="ad-spend-page">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight inline-flex items-center gap-2">
            <Euro className="h-7 w-7" />
            {t("ad_spend.title", "Ad spend")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            {t("ad_spend.subtitle",
                  "Record manual entries or upload CSV exports so the cockpit can show real ROAS / CPL.")}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => setCsvOpen(true)}
                  data-testid="ad-spend-upload-csv">
            <Upload className="h-4 w-4 mr-1" />
            {t("ad_spend.action_upload_csv", "Upload CSV")}
          </Button>
          <Button size="sm" onClick={() => setManualOpen(true)}
                  data-testid="ad-spend-manual-entry">
            <Plus className="h-4 w-4 mr-1" />
            {t("ad_spend.action_manual_entry", "Manual entry")}
          </Button>
        </div>
      </header>

      {/* KPI strip */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3"
               data-testid="ad-spend-kpi-strip">
        <Card data-testid="kpi-total-spend"><CardContent className="p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            {t("ad_spend.total_spend", "Total spend")}
          </div>
          <div className="mt-2 text-2xl font-semibold tabular-nums"
               data-testid="kpi-total-spend-value">
            {fmtEur(totalSpend)}
          </div>
        </CardContent></Card>
        <Card data-testid="kpi-event-count"><CardContent className="p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            {t("ad_spend.event_count", "Events")}
          </div>
          <div className="mt-2 text-2xl font-semibold tabular-nums">
            {fmtInt(eventCount)}
          </div>
        </CardContent></Card>
        <Card data-testid="kpi-avg-per-event"><CardContent className="p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            {t("ad_spend.avg_per_event", "Avg per event")}
          </div>
          <div className="mt-2 text-2xl font-semibold tabular-nums">
            {fmtEur(avgPerEvent)}
          </div>
        </CardContent></Card>
        <Card data-testid="kpi-top-platforms"><CardContent className="p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            {t("cockpit.by_platform_title", "By platform")}
          </div>
          <div className="mt-2 flex flex-wrap gap-1">
            {topPlatforms.length === 0
              ? <span className="text-sm text-muted-foreground">—</span>
              : topPlatforms.map((p) => (
                <Badge key={p.platform} variant="secondary"
                       data-testid={`platform-chip-${p.platform}`}>
                  {p.platform} · {fmtEur(p.total_spend_eur)}
                </Badge>
              ))}
          </div>
        </CardContent></Card>
      </section>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3"
           data-testid="ad-spend-filter-bar">
        <div className="flex gap-1" data-testid="period-selector">
          {PRESETS.map((p) => (
            <Button key={p.key} size="sm"
                    variant={preset === p.key ? "default" : "outline"}
                    onClick={() => setPreset(p.key)}
                    data-testid={`as-period-${p.key}`}>
              {t(`cockpit.period_${p.key}`, p.key)}
            </Button>
          ))}
        </div>
        <Select value={platformFilter} onValueChange={setPlatformFilter}>
          <SelectTrigger className="w-[200px]"
                         data-testid="ad-spend-platform-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {t("ad_spend.filter_all_platforms", "All platforms")}
            </SelectItem>
            {PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="ghost" size="sm" asChild data-testid="ad-spend-cockpit-link">
          <Link to="/app/cockpit" className="text-xs">
            {t("cockpit.title", "Market Intelligence")}
            <ExternalLink className="h-3 w-3 ml-1" />
          </Link>
        </Button>
      </div>

      {/* Events table */}
      <Card data-testid="ad-spend-table-card">
        <CardHeader>
          <CardTitle>{t("ad_spend.title", "Ad spend")}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-sm text-muted-foreground"
                 data-testid="ad-spend-loading">
              {t("common.saving", "Saving…")}
            </div>
          ) : data.items.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center"
                 data-testid="ad-spend-empty">
              {t("ad_spend.empty_state",
                    "No ad-spend events yet. Upload a CSV or create a manual entry.")}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="ad-spend-table">
                <thead>
                  <tr className="text-left text-xs uppercase text-muted-foreground border-b">
                    <th className="py-2 pr-3">{t("ad_spend.table_date", "Date")}</th>
                    <th className="py-2 pr-3">{t("ad_spend.table_campaign", "Campaign")}</th>
                    <th className="py-2 pr-3">{t("ad_spend.table_platform", "Platform")}</th>
                    <th className="py-2 pr-3 text-right">{t("ad_spend.table_spend", "Spend")}</th>
                    <th className="py-2 pr-3 text-right">{t("ad_spend.table_impressions", "Impressions")}</th>
                    <th className="py-2 pr-3 text-right">{t("ad_spend.table_clicks", "Clicks")}</th>
                    <th className="py-2 pr-3 text-right">{t("ad_spend.table_conversions", "Conversions")}</th>
                    <th className="py-2 pr-3">{t("ad_spend.table_source", "Source")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((e) => (
                    <tr key={e.id}
                        className="border-b last:border-0 hover:bg-muted/30"
                        data-testid={`ad-spend-row-${e.id}`}>
                      <td className="py-2 pr-3 tabular-nums">{e.occurred_on}</td>
                      <td className="py-2 pr-3 font-medium truncate max-w-[260px]"
                          title={e.campaign_id}>{e.campaign_id}</td>
                      <td className="py-2 pr-3">
                        <Badge variant="secondary">{e.platform}</Badge>
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {fmtEur(e.spend_eur)}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">{fmtInt(e.impressions)}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{fmtInt(e.clicks)}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{fmtInt(e.conversions)}</td>
                      <td className="py-2 pr-3 text-xs text-muted-foreground">{e.source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.total > data.items.length && (
                <div className="text-xs text-muted-foreground mt-3"
                     data-testid="ad-spend-table-footer">
                  {`Showing ${data.items.length} of ${data.total}.`}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Connect-platform CTA */}
      <Card data-testid="ad-spend-connect-cta">
        <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-medium">
              {t("ad_spend.connect_cta", "Connect an ad platform")}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {t("ad_spend.connect_cta_hint",
                    "Live sync with Meta, Google and LinkedIn lands in Slice C.")}
            </div>
          </div>
          <Button variant="outline" size="sm" asChild>
            <Link to="/app/integrations">
              {t("cockpit.go_to_integrations", "Manage integrations")}
              <ExternalLink className="h-3 w-3 ml-1" />
            </Link>
          </Button>
        </CardContent>
      </Card>

      <ManualEntryDialog open={manualOpen} onClose={() => setManualOpen(false)}
                         onSaved={refresh} />
      <CsvUploadDialog open={csvOpen} onClose={() => setCsvOpen(false)}
                       onUploaded={refresh} />
    </div>
  );
}
